import { useCallback, useRef, useState } from "react";

import {
  getSmileSimulationCapturedImageDataUrl,
  getSmileSimulationUploadImageDataUrl,
} from "../lib/image";
import type {
  CameraFacingMode,
  ResolvedCameraFacingMode,
} from "../model/types";

type UseSmileSimulationCameraOptions = {
  onCameraFailed: (message: string) => void;
  onCameraReady: () => void;
  onCameraRequested: (message: string) => void;
};

const INITIAL_CAMERA_FACING_MODE: CameraFacingMode = "user";

const getResolvedCameraFacingMode = (
  track: MediaStreamTrack,
  fallbackFacingMode: ResolvedCameraFacingMode,
) => {
  const facingMode = track.getSettings().facingMode;

  return facingMode === "user" || facingMode === "environment"
    ? facingMode
    : fallbackFacingMode;
};

const getNextVideoInputDeviceId = (
  deviceIds: string[],
  currentDeviceId: string | null,
) => {
  if (deviceIds.length < 2) {
    return null;
  }

  const currentDeviceIndex = currentDeviceId
    ? deviceIds.findIndex((deviceId) => deviceId === currentDeviceId)
    : -1;

  if (currentDeviceIndex < 0) {
    return deviceIds[0] ?? null;
  }

  return deviceIds[(currentDeviceIndex + 1) % deviceIds.length] ?? null;
};

const isFacingModeConstraintSupported = () =>
  Boolean(navigator.mediaDevices?.getSupportedConstraints?.().facingMode);

const isLikelyMobileDevice = () => {
  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: {
      mobile?: boolean;
    };
  };

  if (typeof navigatorWithUserAgentData.userAgentData?.mobile === "boolean") {
    return navigatorWithUserAgentData.userAgentData.mobile;
  }

  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
};

const getCanSwitchCamera = (videoInputDeviceIds: string[]) =>
  videoInputDeviceIds.length > 1 ||
  isFacingModeConstraintSupported() ||
  isLikelyMobileDevice();

const stopStreamTracks = (stream: MediaStream) => {
  stream.getTracks().forEach((track) => track.stop());
};

export function useSmileSimulationCamera({
  onCameraFailed,
  onCameraReady,
  onCameraRequested,
}: UseSmileSimulationCameraOptions) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRequestIdRef = useRef(0);
  const preferredFacingModeRef = useRef<CameraFacingMode>(
    INITIAL_CAMERA_FACING_MODE,
  );
  const activeDeviceIdRef = useRef<string | null>(null);
  const videoInputDeviceIdsRef = useRef<string[]>([]);
  const [activeFacingMode, setActiveFacingMode] =
    useState<ResolvedCameraFacingMode>("unknown");
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);

  const clearCameraStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    activeDeviceIdRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const cleanupAttachedStream = useCallback((stream: MediaStream) => {
    stopStreamTracks(stream);

    if (streamRef.current === stream) {
      streamRef.current = null;
      activeDeviceIdRef.current = null;
      setActiveFacingMode("unknown");
    }

    if (videoRef.current?.srcObject === stream) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const stopCameraStream = useCallback(() => {
    cameraRequestIdRef.current += 1;
    clearCameraStream();
  }, [clearCameraStream]);

  const resetCameraFacingModePreference = useCallback(() => {
    preferredFacingModeRef.current = INITIAL_CAMERA_FACING_MODE;
    setActiveFacingMode("unknown");
  }, []);

  const syncVideoInputDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      videoInputDeviceIdsRef.current = [];
      setCanSwitchCamera(getCanSwitchCamera([]));
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const nextDeviceIds = devices
        .filter((device) => device.kind === "videoinput")
        .map((device) => device.deviceId)
        .filter(Boolean);

      videoInputDeviceIdsRef.current = nextDeviceIds;
      setCanSwitchCamera(getCanSwitchCamera(nextDeviceIds));

      return nextDeviceIds;
    } catch {
      videoInputDeviceIdsRef.current = [];
      setCanSwitchCamera(getCanSwitchCamera([]));
      return [];
    }
  }, []);

  const handleCameraError = useCallback(
    (error: unknown, requestedFacingMode: CameraFacingMode) => {
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        onCameraFailed("카메라 권한을 허용해 주세요.");
        return;
      }

      if (
        error instanceof DOMException &&
        (error.name === "NotFoundError" ||
          error.name === "OverconstrainedError")
      ) {
        onCameraFailed(
          requestedFacingMode === "environment"
            ? "사용 가능한 후면 카메라를 찾지 못했어요."
            : "사용 가능한 전면 카메라를 찾지 못했어요.",
        );
        return;
      }

      onCameraFailed("카메라를 시작하지 못했어요.");
    },
    [onCameraFailed],
  );

  const attachStream = useCallback(
    async ({
      requestedFacingMode,
      requestId,
      stream,
    }: {
      requestedFacingMode: CameraFacingMode;
      requestId: number;
      stream: MediaStream;
    }) => {
      if (cameraRequestIdRef.current !== requestId) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const video = videoRef.current;

      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const [videoTrack] = stream.getVideoTracks();

      streamRef.current = stream;
      video.srcObject = stream;
      try {
        await video.play();
      } catch (error) {
        cleanupAttachedStream(stream);
        throw error;
      }

      if (cameraRequestIdRef.current !== requestId) {
        cleanupAttachedStream(stream);
        return false;
      }

      const resolvedFacingMode = videoTrack
        ? getResolvedCameraFacingMode(videoTrack, requestedFacingMode)
        : requestedFacingMode;
      const resolvedDeviceId =
        videoTrack?.getSettings().deviceId ?? activeDeviceIdRef.current;

      preferredFacingModeRef.current = requestedFacingMode;
      activeDeviceIdRef.current = resolvedDeviceId ?? null;
      setActiveFacingMode(resolvedFacingMode);
      await syncVideoInputDevices();

      if (cameraRequestIdRef.current !== requestId) {
        cleanupAttachedStream(stream);
        return false;
      }

      onCameraReady();

      return true;
    },
    [cleanupAttachedStream, onCameraReady, syncVideoInputDevices],
  );

  const attemptStartCameraStream = useCallback(
    async ({
      anyCameraFallback = false,
      fallbackDeviceId = null,
      message,
      requestedFacingMode,
      useExactFacingMode,
    }: {
      anyCameraFallback?: boolean;
      fallbackDeviceId?: string | null;
      message: string;
      requestedFacingMode: CameraFacingMode;
      useExactFacingMode: boolean;
    }) => {
      const requestId = cameraRequestIdRef.current + 1;
      cameraRequestIdRef.current = requestId;
      clearCameraStream();

      if (!navigator.mediaDevices?.getUserMedia) {
        if (cameraRequestIdRef.current === requestId) {
          onCameraFailed("이 브라우저에서는 카메라를 사용할 수 없어요.");
        }

        return null;
      }

      onCameraRequested(message);

      const streamRequests: Array<() => Promise<MediaStream>> = [
        () =>
          navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: useExactFacingMode
                ? { exact: requestedFacingMode }
                : { ideal: requestedFacingMode },
            },
          }),
      ];

      if (fallbackDeviceId) {
        streamRequests.push(() =>
          navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              deviceId: { exact: fallbackDeviceId },
            },
          }),
        );
      }

      if (anyCameraFallback) {
        streamRequests.push(() =>
          navigator.mediaDevices.getUserMedia({
            audio: false,
            video: true,
          }),
        );
      }

      let lastError: unknown = null;

      for (const requestStream of streamRequests) {
        try {
          const stream = await requestStream();
          const didAttach = await attachStream({
            requestedFacingMode,
            requestId,
            stream,
          });

          if (!didAttach) {
            return null;
          }

          return true;
        } catch (error) {
          lastError = error;
        }
      }

      if (cameraRequestIdRef.current !== requestId) {
        return null;
      }

      clearCameraStream();
      setActiveFacingMode("unknown");

      return lastError;
    },
    [
      attachStream,
      clearCameraStream,
      onCameraFailed,
      onCameraRequested,
      setActiveFacingMode,
    ],
  );

  const startCameraStream = useCallback(async () => {
    const result = await attemptStartCameraStream({
      anyCameraFallback: true,
      message: "카메라를 준비하고 있어요.",
      requestedFacingMode: preferredFacingModeRef.current,
      useExactFacingMode: false,
    });

    if (result && result !== true) {
      handleCameraError(result, preferredFacingModeRef.current);
    }
  }, [attemptStartCameraStream, handleCameraError]);

  const switchCamera = useCallback(async () => {
    if (!canSwitchCamera) {
      return;
    }

    const previousFacingMode = preferredFacingModeRef.current;
    const previousDeviceId = activeDeviceIdRef.current;
    const nextFacingMode =
      previousFacingMode === "user" ? "environment" : "user";
    const nextDeviceId = getNextVideoInputDeviceId(
      videoInputDeviceIdsRef.current,
      previousDeviceId,
    );
    const switchResult = await attemptStartCameraStream({
      anyCameraFallback: false,
      fallbackDeviceId: nextDeviceId,
      message: "카메라를 전환하고 있어요.",
      requestedFacingMode: nextFacingMode,
      useExactFacingMode: true,
    });

    if (switchResult === true) {
      return;
    }

    const restoreResult = await attemptStartCameraStream({
      anyCameraFallback: true,
      fallbackDeviceId: previousDeviceId,
      message: "카메라를 복구하고 있어요.",
      requestedFacingMode: previousFacingMode,
      useExactFacingMode: false,
    });

    if (restoreResult && restoreResult !== true) {
      handleCameraError(restoreResult, nextFacingMode);
    }
  }, [attemptStartCameraStream, canSwitchCamera, handleCameraError]);

  const captureImage = useCallback(() => {
    const video = videoRef.current;
    const canvas = captureCanvasRef.current;

    if (!video || !canvas) {
      return null;
    }

    return getSmileSimulationCapturedImageDataUrl(video, canvas, {
      shouldMirror: activeFacingMode === "user",
    });
  }, [activeFacingMode]);

  const uploadImage = useCallback(async (file: File) => {
    const canvas = captureCanvasRef.current;

    if (!canvas) {
      return null;
    }

    return getSmileSimulationUploadImageDataUrl(file, canvas);
  }, []);

  return {
    activeFacingMode,
    canSwitchCamera,
    captureCanvasRef,
    captureImage,
    resetCameraFacingModePreference,
    startCameraStream,
    stopCameraStream,
    switchCamera,
    uploadImage,
    videoRef,
  };
}
